import colorPallete, {
    bodyFonts,
    elevation,
    headlineFonts,
    labelFonts,
    themeBreakPointValues,
} from "@DSG/MobiusOverrides/styles/variables";
import fetchIndustryBrandCarouselData from "@DSG/Services/DSGIndustryBrandCarouselService";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { DealerStateContext } from "@insite/client-framework/Store/Data/Dealers/DealersSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import { ImgProps } from "@insite/mobius/Img";
import Img from "@insite/mobius/Img/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { createRef, useCallback, useContext, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

interface CarouseItem {
    locationName: string;
    brandLogoSmallImagePath: string;
    brandLogoLargeImagePath: string;
    brandFeaturedAltText: string;
    brandUrlSegment: string;
}

interface CarouseProps {
    autoplay?: boolean;
    autoPlayInterval?: number;
    showNavigation?: boolean;
    showDots?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    locationdata: getLocation(state),
});

const enum fields {
    title = "title",
    path = "path",
    categoryUrl = "categoryUrl",
    description = "description",
    titleAlignment = "titleAlignment",
    autoplay = "autoplay",
    autoPlayInterval = "autoPlayInterval",
    showNavigation = "showNavigation",
    showDots = "showDots",
    customCSS = "customCSS",
    classNameCarousel = "classNameCarousel",
    maxNumberOfColumns = "maxNumberOfColumns",
}

interface OwnProps extends WidgetProps {
    onClick?: React.EventHandler<React.MouseEvent>;
    fields: {
        [fields.title]: string;
        [fields.categoryUrl]: string;
        [fields.path]: string;
        [fields.description]: string;
        [fields.titleAlignment]: string;
        [fields.autoplay]: false;
        [fields.autoPlayInterval]: 3000;
        [fields.showNavigation]: true;
        [fields.showDots]: true;
        [fields.customCSS]: string;
        [fields.classNameCarousel]: string;
        [fields.maxNumberOfColumns]: number;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ThemeProps<BaseTheme> & HasShellContext;

export interface BrandCarouselStyles {
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    titleSubText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselMainWrapper?: InjectableCss;
    btnHiddenWrapper?: InjectableCss;
    btnHiddenContainer?: InjectableCss;
    brandLinkImage?: InjectableCss;
    viewMoreLinkItem?: InjectableCss;
    viewMoreLink?: LinkPresentationProps;
}

export const brandCarouselStyles: BrandCarouselStyles = {
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    titleText: {
        variant: "h4",
        weight: headlineFonts.headline4SemiBoldLg.fontWeight,
        css: css`
            text-align: center;
            font-size: ${headlineFonts.headline2SemiBoldLg.fontSize};
            line-height: ${headlineFonts.headline2SemiBoldLg.lineHeight};
            margin-bottom: 8px;
            @media only screen and (max-width: 768px) {
                font-size: ${headlineFonts.headline2SemiBoldSm.fontSize};
                line-height: ${headlineFonts.headline2SemiBoldSm.lineHeight};
            }
        `,
    },
    titleSubText: {
        size: bodyFonts.body2RegularSm.fontSize,
        lineHeight: bodyFonts.body2RegularSm.lineHeight,
        color: colorPallete.grey600,
        css: css`
            text-align: center;
        `,
    },
    mainContainer: {
        gap: 0,
        offsetCss: css`
            @media only screen and (max-width: 768px) {
                justify-content: flex-end;
            }
            max-width: 1280px;
            margin-left: auto;
            margin-right: auto;
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            order: -1;
            justify-content: flex-start;
            // padding-left: 16px;

            &.above {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                display: none !important;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                        display: none;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                        display: none;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}

            &.below {
                [data-test-selector="prevBtn"] {
                    padding: 0;
                }
            }
            @media only screen and (max-width: 768px) {
                &:not(.below) {
                    display: none;
                }
            }
        `,
    },
    prevArrowButton: {
        shape: "rounded",
        css: css`
            padding: 0;
            min-width: 40px;
            min-height: 40px;
            border: 1px solid ${colorPallete.grey200};
            background: ${colorPallete.white};
            color: ${colorPallete.grey800};
            border-radius: 48px;
            box-shadow: ${elevation.dp04};
            z-index: 2;

            [class*="ButtonIcon--"] {
                color: ${colorPallete.grey800};
            }

            &:not(:disabled):hover {
                background: ${colorPallete.white};
                color: ${colorPallete.black};
                border: 1px solid ${colorPallete.grey100};

                [class*="ButtonIcon--"] {
                    color: ${colorPallete.black};
                }
            }
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            // padding-right: 16px;
            &.above {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                display: none !important;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                        display: none;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                        display: none;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}

            &.below {
                [data-test-selector="nextBtn"] {
                    padding: 0;
                }
            }
            @media only screen and (max-width: 768px) {
                &:not(.below) {
                    display: none;
                }
            }
        `,
    },
    nextArrowButton: {
        shape: "rounded",
        css: css`
            padding: 0;
            min-width: 40px;
            min-height: 40px;
            border: 1px solid ${colorPallete.grey200};
            background: ${colorPallete.white};
            color: ${colorPallete.grey800};
            border-radius: 48px;
            z-index: 2;
            box-shadow: ${elevation.dp04};
            [class*="ButtonIcon--"] {
                color: ${colorPallete.grey800};
            }

            &:not(:disabled):hover {
                background: ${colorPallete.white};
                color: ${colorPallete.black};
                border: 1px solid ${colorPallete.grey100};

                [class*="ButtonIcon--"] {
                    color: ${colorPallete.black};
                }
            }
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                ])}
            @media only screen and (min-width: 768px) {
                &[data-test-selector="carouselGridItem_crossSells"],
                &[data-test-selector="carouselGridItem_recentlyViewed"] {
                    border-bottom: 1px solid ${colorPallete.grey300};
                    padding-bottom: 10px;
                }
            }
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            margin-top: 24px;
            margin-bottom: 24px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-top: 40px;
                            margin-bottom: 16px;
                            img {
                                max-height: 80px;
                            }
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}

            img {
                height: auto;
            }
            &.lessItemsThanSlideToScroll {
                transform: initial !important;
                justify-content: center;
            }
            > a {
                &:focus {
                    outline-offset: -2px;
                }
                &:focus:not(:focus-visible) {
                    outline-offset: 0;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    gap: 0;
                    > a {
                        margin-left: 20px;
                        margin-right: 20px;
                    }
                `,
            ], "min")}
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;

            @media only screen and (max-width: 768px) {
                min-width: 200px;
                max-width: 200px;
                margin-right: 16px;
            }
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 20px;
            display: flex;
            flex-direction: column;
            @media only screen and (max-width: 768px) {
                padding-left: 0;
                padding-right: 0;
            }
        `,
    },
    brandLinkImage: {
        css: css`
            padding: 24px;

            border: 2px solid ${colorPallete.white};
            border-radius: 8px;
            img {
                width: 146px;
            }
            &:hover {
                border: 2px solid ${colorPallete.grey800};
                box-shadow: ${elevation.dp04};
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding: 0 16px;
                            margin: 2px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    carouselMainWrapper: {
        css: css`
            /* padding: 80px 0;
            margin: 0 -15px; */

            &.brand-expanded-grid-carousel {
                width: calc(100% + 30px);
                margin: 0 -15px;
                [data-test-selector="textBadges"]:not(:empty) {
                    top: -28px;
                    left: -12px;
                }
                .product-image-wrapper {
                    flex: initial;
                    img {
                        max-height: 138px;
                        margin: auto;
                        width: auto;
                    }
                    > a {
                        height: 138px;
                    }
                }
                .product-description-wrapper,
                .product-image-wrapper {
                    padding-top: 0;
                }
                [data-test-selector="slides"] > div {
                    background: ${colorPallete.white};
                    margin-left: 16px;
                }
                [data-test-selector="carousel_slide"] {
                    padding: 32px 16px 16px;
                }
                [data-test-selector*="productCarousel"] {
                    > div {
                        > div {
                            border: none;
                        }
                    }
                }
                [data-test-selector="slides"] > div {
                    max-width: 220px;
                    min-width: 220px;
                    &:not(:last-child) {
                        margin-right: 0;
                    }
                }
                .product-item-wrapper {
                    border: none;
                    padding: 0;
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                }
                .carouselProduct {
                    flex-basis: 100%;
                    max-width: 100%;
                }
                .productBrandNameWrapper {
                    margin: 12px 0 5px;
                    &,
                    span {
                        font: ${labelFonts.label2Regular.fontWeight} ${labelFonts.label2Regular.fontSize} /
                            ${labelFonts.label2Regular.lineHeight} ${labelFonts.label2Regular.fontFamily};
                        color: ${colorPallete.grey600};
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                width: 100%;
                                margin: 0;
                                padding: 48px 0;
                                .signInWrapperPrice {
                                    padding-top: 8px;
                                }
                                .view-product-btn {
                                    .btn {
                                        margin-top: 5px;
                                    }
                                }
                                [data-test-selector="productDescriptionLink"] {
                                    font-size: ${bodyFonts.body2SemiBoldLg.fontSize};
                                    line-height: ${bodyFonts.body2SemiBoldLg.lineHeight};
                                    margin-bottom: 16px;
                                }

                                [data-test-selector="priceIncludeVat"] {
                                    margin-bottom: 24px;
                                }
                                .carouselProduct {
                                    flex-basis: calc(100% - 80px) !important;
                                    max-width: calc(100% - 80px) !important;
                                    padding: 0 18px;
                                }
                                [data-test-selector="slides"] > div {
                                    max-width: 243px;
                                    min-width: 243px;
                                }
                                .arrowsGrid {
                                    max-width: 40px !important;
                                    flex-basis: 40px !important;
                                    display: flex;
                                    button {
                                        border-radius: 50%;
                                        z-index: 401;
                                        padding: 0;
                                        min-height: 40px;
                                        box-shadow: ${elevation.dp04};
                                        &:not(:disabled) {
                                            cursor: pointer;
                                            border: 1px solid ${colorPallete.grey200};
                                            background: ${colorPallete.white};
                                            color: ${colorPallete.grey800};
                                            [class*="ButtonIcon--"] {
                                                color: ${colorPallete.grey800};
                                            }
                                            &:hover {
                                                background: ${colorPallete.white};
                                                color: ${colorPallete.black};
                                                border: 1px solid ${colorPallete.grey100};

                                                [class*="ButtonIcon--"] {
                                                    color: ${colorPallete.black};
                                                }
                                            }
                                        }
                                    }
                                }
                            `,
                            css`
                                margin-bottom: -32px;
                            `,
                        ],
                        "min",
                    )}

                .arrowsGrid {
                    button {
                        min-height: 40px;
                        &:not(:disabled) {
                            background: ${colorPallete.white};
                        }
                        &:disabled {
                            [class*="ButtonIcon--"] {
                                color: ${colorPallete.white};
                            }
                        }
                    }
                    &:last-child,
                    &:first-child {
                        flex-basis: 40px !important;
                        max-width: 40px !important;
                        padding: 0;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                &.brand-carousel {
                                    position: relative;
                                    padding: 40px 20px 0 20px;
                                    .arrowsGrid:not(.below) {
                                        display: flex;
                                        position: absolute;
                                        z-index: 1;
                                        top: auto;
                                        bottom: 24px;
                                        button {
                                            padding: 0;
                                            &,
                                            * {
                                                background: ${colorPallete.white};
                                                color: ${colorPallete.black};
                                            }
                                        }
                                        &:first-child {
                                            left: 24px;
                                            right: auto;
                                            flex-basis: 40px;
                                            max-width: 40px;
                                            padding: 0;
                                        }
                                        &:last-child {
                                            left: auto;
                                            right: 0;
                                            flex-basis: 40px;
                                            max-width: 40px;
                                            padding: 0;
                                        }
                                    }
                                }
                            `,
                        ],
                        "max",
                    )}
                @media print {
                    &.brand-carousel {
                        [data-test-selector="productDescriptionLink"] {
                            height: 65px;
                        }
                        .product-image-wrapper {
                            width: 100%;
                            max-width: 75%;
                            text-align: center;
                            margin: auto;
                        }
                        [data-test-selector="carousel_slide"] {
                            padding-bottom: 0;
                        }

                        padding: 0;
                        /* .arrowsGrid {
                            display: none;
                        } */
                        [data-test-selector="productPrice_unitNetPrice"] {
                            font-size: 20px;
                        }
                        .view-product-btn .btn {
                            display: none;
                        }
                        .carouselProduct {
                            flex-basis: 100%;
                            max-width: 100%;
                            width: 100%;
                            padding: 0;
                            [data-test-selector="slides"] {
                                display: block;
                                transform: initial !important;
                                > div {
                                    width: 30% !important;
                                    vertical-align: top;
                                    display: inline-block;
                                    left: 0 !important;
                                    max-width: 30%;
                                    min-width: 30%;
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
    btnHiddenWrapper: {
        css: css`
            width: 100%;
        `,
    },
    btnHiddenContainer: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-bottom: 40px;
        `,
    },
    viewMoreLinkItem: {
        css: css`
            text-align: center;
            font-weight: bold;
            justify-content: center;
        `,
    },
    viewMoreLink: {
        css: css`
            min-width: 278px;
            display: inline-flex;
            justify-content: center;
            > span[class*="StyledTypography"] {
                color: ${colorPallete.primaryRed500};
            }
            &:hover {
                > span[class*="StyledTypography"] {
                    color: ${colorPallete.white};
                }
            }
        `,
    },
};

export const ContentWrapper = styled.div<InjectableCss & { alignment: string }>`
    display: flex;
    width: 100%;
    justify-content: ${props => props.alignment ?? "flex-start"};
    ${injectCss};
`;

const BrandCarouse = ({ fields, locationdata }: Props) => {
    const afterCarousel = createRef<HTMLSpanElement>();
    const [items, setItems] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const carouselRef = useRef<HTMLDivElement>(null);
    const [carouselItems, setCarouselItems] = useState<JSX.Element>();

    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = useState(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const setCanScroll = () => {
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };
    const dealer = fields.categoryUrl;

    useEffect(() => {
        const fetcBrandsData = async () => {
            try {
                const response = await fetchIndustryBrandCarouselData(dealer);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = (await response.json()) as any[];
                if (data) {
                    setItems(data);
                }
                setIsLoading(false);
            } catch (error) {
                setError(error);
                setIsLoading(false);
            }
        };

        fetcBrandsData();
    }, [dealer]);

    const [activeIndex, setActiveIndex] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(fields.autoplay);

    const handlePrevClick = () => {
        const newIndex = (activeIndex - 1 + items.length) % items.length;
        setActiveIndex(newIndex);
    };

    const handleNextClick = () => {
        setActiveIndex((activeIndex + 1) % items.length);
    };

    const handleDotClick = (index: number) => {
        setActiveIndex(index);
    };

    useEffect(() => {
        if (carouselRef.current) {
            carouselRef.current.style.transform = `translateX(-${activeIndex * 100}%)`;
        }
    }, [activeIndex]);

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    const getSlidesToScroll = useCallback(() => {
        const maxNumberOfColumns = fields.maxNumberOfColumns || 5;
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        const { innerWidth } = window;
        if (innerWidth <= themeBreakPointValues.mobileSmall) {
            localSlidesToScroll = 1;
        } else if (innerWidth < themeBreakPointValues.mobileLarge) {
            localSlidesToScroll = 2;
        } else if (innerWidth < themeBreakPointValues.tabletLarge) {
            localSlidesToScroll = 3;
        } else if (innerWidth < themeBreakPointValues.laptopSmall) {
            localSlidesToScroll = 3;
        } else {
            localSlidesToScroll = 5;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [fields.maxNumberOfColumns]);

    const [slidesToScroll, setSlidesToScroll] = useState(0);

    useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);
    }, [fields.maxNumberOfColumns, windowResizeTime]);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    useEffect(() => {
        if (!embla || !items || items.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            draggable: isTouchDevice(),
            loop: items.length > slidesToScroll,
        });
        setCanScroll();
    }, [embla, slidesToScroll, items]);

    const styles = brandCarouselStyles;
    const title = fields.title ? fields.title : translate("Select Brands in Electrical");
    const description = fields.description
        ? fields.description
        : translate("Not all brands are available in all locations, please contact your local DSG for availability");

    if (items && items.length === 0) {
        return null;
    }
    return (
        <StyledWrapper
            {...customCssWrapper}
            {...styles.carouselMainWrapper}
            className={`brand-expanded-grid-carousel brand-carousel ${fields?.classNameCarousel}`}
        >
            <>
                <SkipNav
                    text={translate("Skip Carousel")}
                    extendedStyles={styles.skipCarouselButton}
                    destination={afterCarousel}
                />
                <ContentWrapper alignment={fields.titleAlignment} className="wrapper">
                    <Typography {...styles.titleText} variant="h3">
                        {title}
                    </Typography>
                </ContentWrapper>

                <ContentWrapper alignment={fields.titleAlignment} className="wrapper">
                    <Typography {...styles.titleSubText}>{description}</Typography>
                </ContentWrapper>

                <GridContainer {...styles.mainContainer}>
                   
                    <GridItem {...styles.carouselGridItem} className="carouselProduct">
                        <StyledWrapper {...styles.carouselWrapper}>
                            <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                                <StyledWrapper
                                    {...styles.carouselSlidesContainer}
                                    data-test-selector="slides"
                                    className={`${
                                        items.length >= slidesToScroll
                                            ? "moreItemsThanSlideToScroll"
                                            : "lessItemsThanSlideToScroll"
                                    }`}
                                >
                                    {items?.map(item => (
                                        <Clickable
                                            key={item.brandUrlSegment}
                                            {...styles.brandLinkImage}
                                            href={item.brandCustomUrl ? item.brandCustomUrl : `/brands/${item.brandUrlSegment}`}
                                        >
                                            <Img
                                                width="146"
                                                height="65"
                                                src={item.brandLogoSmallImagePath}
                                                altText={item.brandImageNameText}
                                                className="brandImage"
                                            />
                                        </Clickable>
                                    ))}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridItem>

                    <GridItem {...styles.prevArrowGridItem} className="arrowsGrid above">
                        {items.length > slidesToScroll && (
                            <Button
                                {...styles.prevArrowButton}
                                onClick={() => embla && embla.scrollPrev()}
                                disabled={!canScrollPrev}
                                data-test-selector="prevBtn"
                                aria-label={translate('Previous Arrow')}
                            >
                                <ButtonIcon src={ChevronLeft} />
                            </Button>
                        )}
                    </GridItem>

                    <GridItem {...styles.nextArrowGridItem} className="arrowsGrid above">
                        {items.length > slidesToScroll && (
                            <Button
                                {...styles.nextArrowButton}
                                onClick={() => embla && embla.scrollNext()}
                                disabled={!canScrollNext}
                                data-test-selector="nextBtn"
                                aria-label={translate('Next Arrow')}
                            >
                                <ButtonIcon src={ChevronRight} />
                            </Button>
                        )}
                    </GridItem>

                    <Hidden above="sm" {...styles.btnHiddenWrapper}>
                        <StyledWrapper {...styles.btnHiddenContainer}>
                            <GridItem {...styles.prevArrowGridItem} className="arrowsGrid below">
                                {items.length > slidesToScroll && (
                                    <Button
                                        {...styles.prevArrowButton}
                                        onClick={() => embla && embla.scrollPrev()}
                                        disabled={!canScrollPrev}
                                        data-test-selector="prevBtn"
                                    >
                                        <ButtonIcon src={ChevronLeft} />
                                    </Button>
                                )}
                            </GridItem>
                            <GridItem {...styles.nextArrowGridItem} className="arrowsGrid below">
                                {items.length > slidesToScroll && (
                                    <Button
                                        {...styles.nextArrowButton}
                                        onClick={() => embla && embla.scrollNext()}
                                        disabled={!canScrollNext}
                                        data-test-selector="nextBtn"
                                    >
                                        <ButtonIcon src={ChevronRight} />
                                    </Button>
                                )}
                            </GridItem>
                        </StyledWrapper>
                    </Hidden>
                </GridContainer>
                <StyledWrapper {...styles.viewMoreLinkItem}>
                    <Link href={fields.path} {...styles.viewMoreLink} className="btn btn-secondary">
                        {translate("View all brands")}
                    </Link>
                </StyledWrapper>
                <span ref={afterCarousel} tabIndex={-1} />
            </>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withIsInShell(withTheme(BrandCarouse))),
    definition: {
        group: "Common",
        icon: "gallery-thumbnails",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                sortOrder: 0,
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                sortOrder: 1,
            },
            {
                name: fields.path,
                displayName: "Brands Url",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                sortOrder: 2,
            },
            {
                name: fields.categoryUrl,
                displayName: "Category URL",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                sortOrder: 3,
            },
            {
                name: fields.titleAlignment,
                displayName: "Title Alignment",
                editorTemplate: "RadioButtonsField",
                defaultValue: "center",
                horizontal: true,
                fieldType: "General",
                sortOrder: 4,

                options: [
                    {
                        displayName: "Left",
                        value: "flex-start",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "flex-end",
                    },
                ],
            },
            {
                name: fields.classNameCarousel,
                displayName: "Carousel Container Class Name",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 5,
            },
        ],
    },
};
export default widgetModule;
